import React from 'react';
import * as logoSmall from '@/assets/logo/logo-small.png';
import * as logoDefault from '@/assets/logo/logo.png';
import { ChargerSVG } from '@/assets/svg/charger-svg';
import { host } from '@/config';
import { clientsUsingSales } from '@/constants/temp_modularization';
import { useAppSelector } from '@/features';
import { getLogo } from '@/features/selectors';
import type { chargesSearchSchemaType } from '@/modules/sales/charges/route';
import type { paymentsSearchSchemaType } from '@/modules/sales/payments/route';
import { UserSessionContext } from '@/providers/UserSessionProvider';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible';
import { CustomLink } from '@/shared/components/ui/custom-link';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '@/shared/components/ui/sidebar';
import { useMatchRoute } from '@tanstack/react-router';
import { Bus, ChartPie, ChevronRight, Cog, FileChartColumn, PlugZap } from 'lucide-react';

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  return (
    <Sidebar id="side-bar" {...props}>
      <SidebarHeader>
        <AppSidebarHeader />
      </SidebarHeader>
      <SidebarContent>
        <AppSidebarContent />
      </SidebarContent>
      <SidebarFooter>
        <AppSidebarFooter />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function AppSidebarHeader() {
  const logo = useAppSelector(getLogo);
  return (
    <SidebarMenu className="$tw-pt-4">
      <img
        src={logoDefault.default}
        alt="EVX"
        title="EVX"
        className="$tw-rounded-lg $tw-transition-all group-data-[collapsible=icon]:$tw-h-0 group-data-[collapsible=icon]:$tw-w-0 group-data-[collapsible=icon]:$tw-overflow-hidden group-data-[collapsible=icon]:$tw-opacity-0"
        hidden={!!logo}
      />
      <img
        src={logoSmall.default}
        alt="EVX"
        title="EVX"
        className="$tw-h-8 $tw-w-8 $tw-rounded-sm $tw-opacity-0 $tw-transition-all group-data-[collapsible=icon]:$tw-opacity-100"
        hidden={!!logo}
      />
      <img
        src={logo}
        alt="EVX"
        title="EVX"
        className="$tw-px-12 $tw-pb-6 $tw-transition-all group-data-[collapsible=icon]:$tw-overflow-hidden group-data-[collapsible=icon]:$tw-p-0"
        hidden={!logo}
      />
    </SidebarMenu>
  );
}

function AppSidebarContent() {
  const user = React.useContext(UserSessionContext).user;
  const matchRoute = useMatchRoute();

  const useCollapsibleState = (path: string) => {
    const isActive = !!matchRoute({ to: path, fuzzy: true });
    const [isOpen, setIsOpen] = React.useState(isActive);

    React.useEffect(() => {
      setIsOpen(isActive);
    }, [isActive]);

    return [isOpen, setIsOpen] as const;
  };

  const [isChargeCenterOpen, setChargeCenterOpen] = useCollapsibleState('/charge-center');
  const [isFleetOpen, setFleetOpen] = useCollapsibleState('/fleet');
  const [isSalesOpen, setSalesOpen] = useCollapsibleState('/sales');
  const [isAdminOpen, setAdminOpen] = useCollapsibleState('/admin');

  return (
    <SidebarGroup>
      <SidebarMenu>
        <Collapsible
          className="$tw-group/collapsible"
          open={isChargeCenterOpen}
          onOpenChange={setChargeCenterOpen}
          asChild
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                tooltip="Centro de carga"
                className={
                  matchRoute({ to: '/charge-center', fuzzy: true }) ? '$tw-bg-secondary' : ''
                }
              >
                <ChargerSVG className="$tw-h-[1em] $tw-w-[1em]" />
                <span className="$tw-truncate">Centro de carga</span>
                <ChevronRight className="$tw-ml-auto $tw-transition-transform $tw-duration-200 group-data-[state=open]/collapsible:$tw-rotate-90" />
                <span className="$tw-sr-only">Toggle</span>
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent className="group-data-[collapsible=icon]:$tw-hidden">
              <SidebarMenuSub>
                <SidebarMenuSubItem
                  key="grid-connections"
                  className={
                    matchRoute({ to: '/charge-center' })
                      ? '$tw-border-l-2 $tw-border-secondary'
                      : ''
                  }
                >
                  <SidebarMenuSubButton asChild>
                    <CustomLink to="/charge-center">
                      <span className="$tw-truncate">Monitor de empalmes</span>
                    </CustomLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem
                  key="charge-center-map"
                  className={
                    matchRoute({ to: '/charge-center/map', fuzzy: true })
                      ? '$tw-border-l-2 $tw-border-secondary'
                      : ''
                  }
                >
                  <SidebarMenuSubButton asChild>
                    <CustomLink to="/charge-center/map">
                      <span className="$tw-truncate">Mapa</span>
                    </CustomLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem
                  key="transaction-log"
                  className={
                    matchRoute({ to: '/charge-center/assets-transactions', fuzzy: true })
                      ? '$tw-border-l-2 $tw-border-secondary'
                      : ''
                  }
                >
                  <SidebarMenuSubButton asChild>
                    <CustomLink to="/charge-center/assets-transactions">
                      <span className="$tw-truncate">Registro de transacciones</span>
                    </CustomLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
        <SidebarMenuItem>
          <SidebarMenuButton tooltip="Análisis" asChild>
            <CustomLink to="/analytics" activeProps={{ className: '$tw-bg-secondary' }}>
              <ChartPie className="$tw-h-[1em] $tw-w-[1em]" />
              <span>Análisis</span>
            </CustomLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
        {user?.is_staff ? (
          <SidebarMenuItem>
            <SidebarMenuButton tooltip="Gestión de demanda" asChild>
              <CustomLink to="/remote-management" activeProps={{ className: '$tw-bg-secondary' }}>
                <PlugZap className="$tw-h-[1em] $tw-w-[1em]" />
                <span className="$tw-truncate">Gestión de demanda</span>
              </CustomLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ) : null}
        <Collapsible
          className="$tw-group/collapsible"
          open={isFleetOpen}
          onOpenChange={setFleetOpen}
          asChild
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                tooltip="Flota"
                className={matchRoute({ to: '/fleet', fuzzy: true }) ? '$tw-bg-secondary' : ''}
              >
                <Bus className="$tw-h-[1em] $tw-w-[1em]" />
                <span className="$tw-truncate">Flota</span>
                <ChevronRight className="$tw-ml-auto $tw-transition-transform $tw-duration-200 group-data-[state=open]/collapsible:$tw-rotate-90" />
                <span className="$tw-sr-only">Toggle</span>
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent className="group-data-[collapsible=icon]:$tw-hidden">
              <SidebarMenuSub>
                <SidebarMenuSubItem
                  key="fleet-map"
                  className={
                    matchRoute({ to: '/fleet/map', fuzzy: true })
                      ? '$tw-border-l-2 $tw-border-secondary'
                      : ''
                  }
                >
                  <SidebarMenuSubButton asChild>
                    <CustomLink to="/fleet/map">
                      <span className="$tw-truncate">Mapa</span>
                    </CustomLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem
                  key="fleet-summary"
                  className={
                    matchRoute({ to: '/fleet/summary', fuzzy: true })
                      ? '$tw-border-l-2 $tw-border-secondary'
                      : ''
                  }
                >
                  <SidebarMenuSubButton asChild>
                    <CustomLink to="/fleet/summary">
                      <span className="$tw-truncate">Resumen</span>
                    </CustomLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
        {clientsUsingSales.includes(host) && user?.is_staff ? (
          <Collapsible
            className="$tw-group/collapsible"
            open={isSalesOpen}
            onOpenChange={setSalesOpen}
            asChild
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip="Ventas"
                  className={matchRoute({ to: '/sales', fuzzy: true }) ? '$tw-bg-secondary' : ''}
                >
                  <FileChartColumn className="$tw-h-[1em] $tw-w-[1em]" />
                  <span className="$tw-truncate">Ventas</span>
                  <ChevronRight className="$tw-ml-auto $tw-transition-transform $tw-duration-200 group-data-[state=open]/collapsible:$tw-rotate-90" />
                  <span className="$tw-sr-only">Toggle</span>
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent className="group-data-[collapsible=icon]:$tw-hidden">
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    key="charges"
                    className={
                      matchRoute({ to: '/sales/charges', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/sales/charges" search={{} as chargesSearchSchemaType}>
                        <span className="$tw-truncate">Cargas</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem
                    key="payments"
                    className={
                      matchRoute({ to: '/sales/payments', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/sales/payments" search={{} as paymentsSearchSchemaType}>
                        <span className="$tw-truncate">Cobros</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ) : null}
        {user?.is_staff ? (
          <Collapsible
            className="$tw-group/collapsible"
            open={isAdminOpen}
            onOpenChange={setAdminOpen}
            asChild
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip="Administración"
                  className={matchRoute({ to: '/admin', fuzzy: true }) ? '$tw-bg-secondary' : ''}
                >
                  <Cog className="$tw-h-[1em] $tw-w-[1em]" />
                  <span className="$tw-truncate">Administración</span>
                  <ChevronRight className="$tw-ml-auto $tw-transition-transform $tw-duration-200 group-data-[state=open]/collapsible:$tw-rotate-90" />
                  <span className="$tw-sr-only">Toggle</span>
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent className="group-data-[collapsible=icon]:$tw-hidden">
                <SidebarMenuSub>
                  <SidebarMenuSubItem
                    key="contracts"
                    className={
                      matchRoute({ to: '/admin/contracts', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/admin/contracts">
                        <span className="$tw-truncate">Contratos</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem
                    key="user-management"
                    className={
                      matchRoute({ to: '/admin/user-management', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/admin/user-management">
                        <span className="$tw-truncate">Gestión de usuarios</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem
                    key="charge-points-management"
                    className={
                      matchRoute({ to: '/admin/charge-point-management', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/admin/charge-point-management">
                        <span className="$tw-truncate">Gestión de cargadores</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem
                    key="transactions"
                    className={
                      matchRoute({ to: '/charge-center/assets-transactions', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/charge-center/assets-transactions">
                        <span className="$tw-truncate">Registro de transacciones</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem
                    key="fleet-management"
                    className={
                      matchRoute({ to: '/admin/fleet-management', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/admin/fleet-management">
                        <span className="$tw-truncate">Gestión de flota</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem
                    key="fleet-trip-history"
                    className={
                      matchRoute({ to: '/admin/fleet-trip-history', fuzzy: true })
                        ? '$tw-border-l-2 $tw-border-secondary'
                        : ''
                    }
                  >
                    <SidebarMenuSubButton asChild>
                      <CustomLink to="/admin/fleet-trip-history">
                        <span className="$tw-truncate">Historial de viajes</span>
                      </CustomLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ) : null}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function AppSidebarFooter() {
  const user = React.useContext(UserSessionContext).user;
  return (
    <SidebarMenu>
      <SidebarMenuItem className="$tw-mx-auto $tw-overflow-hidden $tw-transition-all $tw-duration-300 group-data-[collapsible=icon]:$tw-max-h-0 group-data-[collapsible=icon]:$tw-opacity-0">
        <span className="$tw-truncate $tw-text-xs">{user?.email}</span>
      </SidebarMenuItem>
      <SidebarMenuItem className="$tw-mx-auto $tw-overflow-hidden $tw-transition-all $tw-duration-300 group-data-[collapsible=icon]:$tw-max-h-0 group-data-[collapsible=icon]:$tw-opacity-0">
        <span className="$tw-truncate $tw-text-xs">v{APP_VERSION}</span>
      </SidebarMenuItem>
      <SidebarMenuItem className="$tw-mx-auto $tw-overflow-hidden $tw-transition-all $tw-duration-300 group-data-[collapsible=icon]:$tw-max-h-0 group-data-[collapsible=icon]:$tw-opacity-0">
        <span className="$tw-truncate $tw-text-xs">
          Copyright Enerlink &copy; {new Date().getFullYear()}
        </span>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
